import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useState } from 'react';
import { normalizeCountryCode } from '../../lib/geo/localeCountry';

export const PRICING_COUNTRY_KEY = 'flowsellai.pricing.country.v1';

/**
 * ISO 3166 reserves ZZ for "unspecified". The buyer sends it to say they are
 * not in a localized market and want the global (USD) catalog — an explicit
 * choice that outranks IP detection on the server.
 */
export const GLOBAL_MARKET_SELECTION = 'ZZ';

/**
 * Normalizes a candidate selection to a value the pricing API accepts, or null
 * when it is not usable (so geo detection stays in charge).
 */
export function normalizeSelection(value?: string | null): string | null {
  const normalized = value?.trim().toUpperCase();
  if (!normalized) return null;
  if (normalized === GLOBAL_MARKET_SELECTION) return GLOBAL_MARKET_SELECTION;
  return normalizeCountryCode(normalized) ?? null;
}

export interface CountryPreference {
  /** The buyer's explicit choice, or null when they have not made one. */
  selection: string | null;
  select: (value: string | null) => void;
  /** False until storage has been read, so the UI can avoid a currency flicker. */
  isLoaded: boolean;
}

/**
 * Remembers the buyer's explicit country/market choice across visits.
 *
 * Geo detection is always a guess — a Colombian on a US VPN, or a traveler,
 * gets the wrong currency and needs a way to correct it. This persists that
 * correction so they only make it once. Storage failures are non-fatal: the
 * selection simply does not survive the session.
 */
export function useCountryPreference(): CountryPreference {
  const [selection, setSelection] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const stored = await AsyncStorage.getItem(PRICING_COUNTRY_KEY);
        if (!cancelled) {
          setSelection(normalizeSelection(stored));
        }
      } catch {
        // A read failure just means no remembered choice.
      } finally {
        if (!cancelled) {
          setIsLoaded(true);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  const select = useCallback((value: string | null) => {
    if (value === null) {
      setSelection(null);
      void AsyncStorage.removeItem(PRICING_COUNTRY_KEY).catch(() => undefined);
      return;
    }

    const normalized = normalizeSelection(value);
    if (!normalized) {
      return;
    }

    setSelection(normalized);
    void AsyncStorage.setItem(PRICING_COUNTRY_KEY, normalized).catch(() => undefined);
  }, []);

  return { selection, select, isLoaded };
}
